/**
 * Recently-Purchased full-list page (DEV-46921 — reached via
 * "View All" from the Home recently-purchased carousel).
 *
 * Compact row layout matching the deployed mockup (Railway):
 *   - Small square placeholder icon on the left.
 *   - Product name (bold), then a metadata line with supplier,
 *     location marker + branch, distance, and stock pill.
 *   - Price + clock icon + "13 Jul, 09:15" timestamp on a line under
 *     the metadata row.
 *   - Heart (outline / filled) on the right; toggling favourites the
 *     RAW product the engineer bought.
 *
 * Deduped by product identity — buying the same item ten times
 * surfaces one tile, newest occurrence wins. Capped at 40 unique
 * items after dedupe.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Clock, Heart, Loader2, MapPin, Package, Wrench } from 'lucide-react'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button } from '../../components/radix'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useFavouriteToggle } from '../../components/engineer/favourites/useFavouriteToggle'
import { useRecentlyPurchased } from '../../auth/hooks/useRecentlyPurchased'
import type { RecentlyPurchasedTile } from '../../types'
import { IN_STOCK_LABEL } from '../../constants/display'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

function formatCompactStamp(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const day = d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      timeZone: 'Europe/London',
    })
    const time = d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/London',
    })
    return `${day}, ${time}`
  } catch {
    return ''
  }
}

export function RecentlyPurchasedPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const raw = sessionStorage.getItem('engineerSession')
    if (!raw) {
      navigate(`/${tenantSlug}/engineer`, { replace: true })
      return
    }
    try {
      JSON.parse(raw) as EngineerSession
      setReady(true)
    } catch {
      navigate(`/${tenantSlug}/engineer`, { replace: true })
    }
  }, [navigate, tenantSlug])

  const { data: items, isLoading } = useRecentlyPurchased()

  if (!ready) return null

  const rows = items ?? []

  return (
    // Same as /favourites — this page has its own sticky header, so
    // we suppress the shared EngineerHeader to avoid stacking two
    // dark bars (the mockup shows one header only).
    <EngineerLayout hideHeader>
      {/* Sticky page header — dark nav-bg, back arrow, title + count.
       * Matches the Favourites page for visual consistency. */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 hover:bg-nav-hover rounded-xl transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-bold">Recently purchased</h1>
            {rows.length > 0 && (
              <span className="text-sm opacity-60">({rows.length})</span>
            )}
          </div>
        </div>
      </div>

      <main className="bg-gray-50 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
                <Package className="w-10 h-10 text-brand-primary" />
              </div>
              <h2 className="text-gray-900 text-xl font-bold mb-3 text-center">
                Nothing purchased yet
              </h2>
              <p className="text-gray-500 text-base text-center mb-6 max-w-sm">
                Items from your orders will show up here so you can re-order
                them in a tap.
              </p>
              <Button onClick={() => navigate(`/${tenantSlug}/engineer/search`)}>
                Search products
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row) => (
                <RecentlyPurchasedRow
                  key={`rp-${row.orderItemId}`}
                  row={row}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </EngineerLayout>
  )
}

/**
 * Compact horizontal row for a single recently-purchased item.
 *
 * Matches the deployed mockup's `/recently-purchased` design:
 *   Icon | Name                                            Heart
 *        | Supplier · 📍 Branch · 2.3 mi · ● In Stock
 *        | £12.50                            🕒 13 Jul, 09:15
 */
function RecentlyPurchasedRow({ row }: { row: RecentlyPurchasedTile }) {
  const navigate = useNavigate()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const { isFavourited } = useFavourites()
  const toggleFavourite = useFavouriteToggle()

  const listing = row.listing

  // Tap the row → open the supplier product page for the raw the
  // engineer bought (falls back to master detail if the raw is gone).
  const openDetail = () => {
    if (listing.targetType === 'raw' && listing.rawProductId != null) {
      const suffix = listing.branchId ? `?branchId=${listing.branchId}` : ''
      navigate(
        `/${tenantSlug}/engineer/supplier-product/${listing.rawProductId}${suffix}`,
      )
    } else if (listing.masterProductId != null) {
      navigate(`/${tenantSlug}/engineer/product/${listing.masterProductId}`)
    }
  }

  const heartTarget =
    listing.targetType === 'raw' && listing.rawProductId != null
      ? { type: 'raw' as const, id: listing.rawProductId }
      : listing.masterProductId != null
        ? { type: 'master' as const, id: listing.masterProductId }
        : null
  const favourited = heartTarget ? isFavourited(heartTarget) : false

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="flex items-start gap-4 p-4">
        {/* Placeholder icon (small square, matches mockup) */}
        <button
          onClick={openDetail}
          className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
          aria-label={`Open ${listing.displayName ?? 'product'} detail`}
        >
          {listing.imageUrl ? (
            <img
              src={listing.imageUrl}
              alt={listing.displayName ?? 'Product image'}
              className="w-full h-full object-contain"
            />
          ) : (
            <Wrench className="w-7 h-7 text-gray-300" />
          )}
        </button>

        {/* Main info column */}
        <div className="flex-1 min-w-0">
          <button onClick={openDetail} className="text-left w-full">
            <h3 className="text-gray-900 font-bold text-sm leading-tight line-clamp-2">
              {listing.displayName ?? 'Unnamed product'}
            </h3>

            {/* Supplier · MapPin · branch · distance · stock pill */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-400 mt-1.5">
              {listing.supplierName && (
                <span className="font-medium text-gray-500">
                  {listing.supplierName}
                </span>
              )}
              {listing.branchName && (
                <>
                  <span>·</span>
                  <MapPin className="w-3 h-3 text-brand-primary" />
                  <span className="text-gray-500">{listing.branchName}</span>
                </>
              )}
              {listing.distanceMiles != null && (
                <>
                  <span>·</span>
                  <span>{listing.distanceMiles.toFixed(1)} mi</span>
                </>
              )}
              {listing.stockStatus && (
                <>
                  <span>·</span>
                  <StockPill status={listing.stockStatus} />
                </>
              )}
            </div>

            {/* Price + timestamp row.
             * Mockup keeps them next to each other on the left (not
             * split with `justify-between`) — the timestamp is a
             * secondary label right beside the price, not a right-
             * aligned attribute. `gap-3` gives roughly the same
             * spacing the mockup screenshot shows. */}
            <div className="flex items-center gap-3 mt-2">
              <span className="font-bold text-base text-brand-primary">
                {listing.price != null ? `£${listing.price.toFixed(2)}` : '—'}
              </span>
              <div className="flex items-center gap-1 text-gray-400 text-xs">
                <Clock className="w-3 h-3" />
                <span>{formatCompactStamp(row.purchasedAt)}</span>
              </div>
            </div>
          </button>
        </div>

        {/* Favourite toggle (right-aligned).
         * Recently-purchased rows favourite the RAW product (that
         * supplier's specific SKU) so a later tap on the Home
         * Favourites tile reopens the same supplier detail page. */}
        {heartTarget && (
          <button
            onClick={() =>
              toggleFavourite({
                target: heartTarget,
                productName: listing.displayName ?? 'Unnamed product',
              })
            }
            // The ::before extends the tap area to 44px without changing the look.
            className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0 before:absolute before:-inset-1 before:content-['']"
            aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
            aria-pressed={favourited}
          >
            <Heart
              className={`w-5 h-5 ${favourited ? 'text-brand-primary' : 'text-gray-400'}`}
              fill={favourited ? 'currentColor' : 'none'}
            />
          </button>
        )}
      </div>
    </div>
  )
}

/** Inline stock pill used in the metadata row. */
function StockPill({ status }: { status: string }) {
  if (status === 'in_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-green-600">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        {IN_STOCK_LABEL}
      </span>
    )
  }
  if (status === 'low_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-600">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Low stock
      </span>
    )
  }
  if (status === 'out_of_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-red-600">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Out of stock
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
      Checking…
    </span>
  )
}
